<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
	modelValue: {
		type: String,
		default: '',
		required: false,
	},
	error: {
		type: [String, Boolean],
		default: false,
		required: false,
	},
	id: {
		type: String,
		default: null,
		required: false,
	},
	name: {
		type: String,
		default: null,
		required: false,
	},
	type: {
		type: String,
		default: 'text',
		required: false,
	},
	labelName: {
		type: String,
		required: true,
	},
	placeHolder: {
		type: String,
		default: '',
		required: false,
	},
	disabled: {
		type: Boolean,
		default: false,
		required: false,
	},
	isLoading: {
		type: Boolean,
		default: false,
		required: false,
	},
	backgroundColor: {
		type: String,
		default: '',
		required: false,
	},
	disabledBackgroundColor: {
		type: String,
		default: 'bg-gray-200',
		required: false,
	},
	showErrorAsLabel: {
		type: Boolean,
		default: false,
		required: false,
	},
	showLabel: {
		type: Boolean,
		default: true,
		required: false,
	},
	showError: {
		type: Boolean,
		default: true,
		required: false,
	},
	isDisabled: {
		type: Boolean,
		default: false,
		required: false,
	},
	isCapitalised: {
		type: Boolean,
		default: false,
		required: false,
	},
	autoCompleteName: {
		type: String,
		default: null,
		required: false,
	},
	minlength: {
		type: Number,
		default: 1,
		required: false,
	},
	maxlength: {
		type: Number,
		default: 524288,
		required: false,
	},
	inputmode: {
		type: String,
		default: 'text',
		require: false,
	},
})

const emit = defineEmits(['update:modelValue', 'changeHandler', 'onClick'])

const fieldId = computed(() => props.id ?? props.name)

const fieldAutoCompleteValue = computed(() => {
	if (props.autoCompleteName) {
		return props.autoCompleteName
	}
	return props.name
})

function onChange(event) {
	emit('update:modelValue', event.target.value)
	emit('changeHandler', event.target.value)
}

function onClick() {
	emit('onClick')
}

const input = ref(null)

const placeholderName = computed(() => {
	if (!props.showLabel) {
		return props.labelName
	}
	return props.placeHolder ?? ''
})
</script>

<template>
	<div class="flex flex-col">
		<div
			v-bind="$attrs"
			class="input-field relative"
			:class="[error ? 'error' : '']"
		>
			<input
				:id="fieldId"
				ref="input"
				class="text-idrive-dark ring-none block w-full font-heading appearance-none rounded border p-3.5 shadow-none outline-none ring-transparent focus:outline-none"
				:name="name"
				:class="[
					disabled ? disabledBackgroundColor : backgroundColor,
					error
						? 'border-idrive-hover border-opacity-80'
						: 'border-gray-200 ',
					showLabel ? 'px-3 pt-7' : 'px-3',
					isCapitalised ? 'uppercase placeholder:normal-case' : '',
				]"
				:placeholder="placeholderName"
				:value="modelValue"
				:type="type ?? 'text'"
				:inputmode="inputmode"
				:disabled="isDisabled"
				:autocomplete="fieldAutoCompleteValue"
				:minlength="minlength"
				:maxlength="maxlength"
				@input="onChange"
				@click="onClick"
			/>
			<label
				v-if="showLabel"
				:for="fieldId"
				class="pointer-events-none absolute top-3.5 text-xs text-gray-600 origin-top-left -translate-y-1/2 px-3 duration-300"
			>
				{{
					typeof error === 'string' && error && showErrorAsLabel
						? error
						: labelName
				}}
			</label>
			<span
				v-if="isLoading"
				class="multiselect-spinner absolute right-4 top-4"
			/>
		</div>
		<div
			v-if="error && showError"
			class="mt-1.5 flex flex-row justify-start space-x-1"
		>
			<div class="text-sm text-idrive-hover opacity-70">
				{{ error }}
			</div>
		</div>
	</div>
</template>
